import React from 'react';
import {
  Maximize, PictureInPicture2, Captions, Gauge,
} from 'lucide-react';
import {
  Play as PhPlay, Pause as PhPause,
  FastForward as PhFastForward, Rewind as PhRewind,
  SpeakerHigh as PhSpeakerHigh, SpeakerSimpleX as PhSpeakerX,
  ArrowsOutSimple as PhFullscreen,
  PictureInPicture as PhPip,
  ClosedCaptioning as PhCaptions,
  Gauge as PhGauge,
} from '@phosphor-icons/react';
import { SafariSkipIcon } from './SafariSkipIcon';

export type ControlsTheme = 'minimal' | 'solid' | 'duotone' | 'bold' | 'safari';

export type IconName =
  | 'play'
  | 'pause'
  | 'skipBack'
  | 'skipForward'
  | 'volume'
  | 'volumeX'
  | 'fullscreen'
  | 'pip'
  | 'captions'
  | 'gauge';

interface ThemedIconProps {
  name: IconName;
  theme: ControlsTheme;
  className?: string;
  style?: React.CSSProperties;
}

// SVG paths idênticos aos usados no runner.js do playerjs (viewBox 24x24).
// Mantêm a paridade visual entre o player do front e o player.js externo.
// Filled icons (Material Design style):
const NATIVE_PATHS_FILLED: Record<string, string> = {
  play: 'M8 5v14l11-7z',
  pause: 'M6 19h4V5H6zm8-14v14h4V5z',
  skipBack: 'M11 18V6l-8.5 6zm.5-6 8.5 6V6z',
  skipForward: 'M13 6v12l8.5-6zm-1.5 6L3 18V6z',
  volume: 'M3 9v6h4l5 5V4L7 9z',
  // Material Design VolumeOff (alto-falante + X), 24x24 viewBox.
  volumeX:
    'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z',
};

// Componente que renderiza os SVGs nativos (matching playerjs).
const NativeIcon: React.FC<{ name: IconName; className?: string; style?: React.CSSProperties }> = ({
  name,
  className,
  style,
}) => {
  const d = NATIVE_PATHS_FILLED[name];
  if (!d) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ fill: 'currentColor', ...style }}
    >
      <path d={d} />
    </svg>
  );
};

export const ThemedIcon: React.FC<ThemedIconProps> = ({
  name,
  theme,
  className = 'h-5 w-5',
  style,
}) => {
  if (theme === 'minimal') {
    // Para play/pause/skip/volume usa os SVGs nativos do playerjs.
    if (NATIVE_PATHS_FILLED[name]) {
      return <NativeIcon name={name} className={className} style={style} />;
    }
    // Demais (fullscreen, pip, captions, gauge) seguem Lucide outline.
    const map: Record<IconName, React.ElementType> = {
      play: PhPlay, pause: PhPause, skipBack: PhRewind, skipForward: PhFastForward,
      volume: PhSpeakerHigh, volumeX: PhSpeakerX,
      fullscreen: Maximize,
      pip: PictureInPicture2,
      captions: Captions,
      gauge: Gauge,
    };
    const Cmp = map[name];
    return <Cmp className={className} style={style} />;
  }

  // Phosphor compartilha os mesmos componentes — muda apenas o weight prop.
  const phMap: Record<IconName, React.ElementType> = {
    play: PhPlay,
    pause: PhPause,
    skipBack: PhRewind,
    skipForward: PhFastForward,
    volume: PhSpeakerHigh,
    volumeX: PhSpeakerX,
    fullscreen: PhFullscreen,
    pip: PhPip,
    captions: PhCaptions,
    gauge: PhGauge,
  };

  // Tema "Safari": os botões de avançar/voltar viram a seta circular com "10"
  // (gobackward.10 / goforward.10) e os demais ícones usam Phosphor "fill"
  // (filled, no espírito dos SF Symbols). O botão redondo translúcido com blur
  // vem do getThemeStyles/PlayButton, não daqui.
  if (theme === 'safari') {
    if (name === 'skipBack') {
      return <SafariSkipIcon direction="back" className={className} style={style} />;
    }
    if (name === 'skipForward') {
      return <SafariSkipIcon direction="forward" className={className} style={style} />;
    }
    const SafariCmp = phMap[name];
    return <SafariCmp className={className} style={style} weight="fill" />;
  }

  const Cmp = phMap[name];
  const weight = theme === 'solid' ? 'fill' : theme === 'bold' ? 'bold' : 'duotone';
  return <Cmp className={className} style={style} weight={weight as any} />;
};
